import { types, clone, flow, getParent, getSnapshot, isStateTreeNode } from 'mobx-state-tree';
import { User, NullUser } from './User';

// DynamoDB can return null for optional fields; MST's types.maybe only accepts undefined (not null).
// These helpers coerce null → undefined so MST doesn't throw a union mismatch error.
const toMaybeString = v => (v == null || typeof v === 'string') ? (v ?? undefined) : undefined;
const toMaybeNumber = v => (v == null) ? undefined : (typeof v === 'number' ? v : (isNaN(Number(v)) ? undefined : Number(v)));
const toMaybeBoolean = v => (v == null) ? undefined : Boolean(v);

const SESSION_TYPES = ['Elevator Pitch', 'Phone Call', 'Casual Encounter', 'Presentation', 'Online Video Conference', 'Sales Presentation', 'Sales Presentation / Demo', 'Investor Pitch', 'Objection Handling', 'Document Defense', 'Messaging Defense', 'Deal Coaching', 'Prospect Prep', 'Live Meeting'];
const SESSION_TYPE_SET = new Set(SESSION_TYPES);
// 'Live Meeting' is written by the live-meeting analyzer (analyzer.js); without
// it here, normalizeSubject silently relabelled every live meeting as
// 'Company Overview'. (The scheduling form has its own separate option list.)
const SUBJECTS = ['Company Overview', 'Company Overview (for MVP/Rollout)', 'Offerings', 'Objection Handling', 'Document Defense', 'Messaging Defense', 'Deal Coaching', 'Prospect Prep', 'Live Meeting'];
const SUBJECT_SET = new Set(SUBJECTS);

const normalizeSessionType = raw => {
    if (raw === 'Q&A Practice') return 'Objection Handling';
    if (raw === 'Deal Coaching') return 'Document Defense';
    if (raw === 'Prospect Prep') return 'Messaging Defense';
    if (raw == null) return undefined;
    return SESSION_TYPE_SET.has(raw) ? raw : 'Phone Call';
};

const normalizeSubject = raw => {
    if (raw === 'Q&A Practice') return 'Objection Handling';
    if (raw === 'Document Q&A') return 'Document Defense';
    if (raw === 'Deal Coaching') return 'Document Defense';
    if (raw === 'Prospect Prep') return 'Messaging Defense';
    if (raw == null) return 'Company Overview';
    return SUBJECT_SET.has(raw) ? raw : 'Company Overview';
};

const _pointsDescription = types.model('PointsDescription', {
    category: types.string,
    pointsAwarded: types.number,
    description: types.string,
    maxPoints: types.maybe(types.number),
    skipped: types.maybe(types.boolean),
    notApplicable: types.maybe(types.boolean),
    keyPointsCovered: types.maybe(types.array(types.string)),
    keyPointsMissed: types.maybe(types.array(types.string))
});

// Categories the rep had no opportunity to score on (e.g. "Questions and
// Answers" on Level 1 sessions where Murphy never asks questions) belong in
// NEITHER report column. New records carry notApplicable from the grader;
// others are caught by the mandated neutral wording (grading hard rule 3
// requires that exact sentence, alone, when no questions were asked) — hidden
// REGARDLESS of score: the interactions category on Level 2 sessions can
// award points alongside the sentence, which rendered "Murphy did not ask any
// questions" as a green positive. Points still count; only the display goes.
const NO_OPPORTUNITY_TEXT = /(no questions were asked during this session|murphy did not ask any questions)/i;
const isDisplayableCategory = point => {
    if (point.notApplicable) return false;
    if (NO_OPPORTUNITY_TEXT.test(point.description || '')) return false;
    return true;
};

export const Assessment = types.model('Assessment', {
    id: types.string,
    aiGenerated: types.optional(types.boolean, false),
    companyId: types.string,
    salesperson: User,
    sessionType: types.optional(types.enumeration(SESSION_TYPES), 'Phone Call'),
    subject: types.optional(types.enumeration(SUBJECTS), 'Company Overview'),
    secondarySubject: types.maybe(types.string),
    reviewDateTime: types.maybe(types.string),
    videoName: types.maybe(types.string),
    completedDateTime: types.maybe(types.string),
    additionalAttendees: types.maybe(types.string),
    offeringPositives: types.maybe(types.string),
    offeringAreasForImprovement: types.maybe(types.string),
    companyBackgroundPositives: types.maybe(types.string),
    companyBackgroundAreasForImprovement: types.maybe(types.string),
    differentiatorsPositives: types.maybe(types.string),
    differentiatorsAreasForImprovement: types.maybe(types.string),
    prospectProfilePositives: types.maybe(types.string),
    prospectProfileAreasForImprovement: types.maybe(types.string),
    topCompetitorsPositives: types.maybe(types.string),
    topCompetitorsAreasForImprovement: types.maybe(types.string),
    companyOverviewPositives: types.maybe(types.string),
    companyOverviewAreasForImprovement: types.maybe(types.string),
    positives: types.maybe(types.array(types.string)),
    areasForImprovement: types.maybe(types.array(types.string)),
    grade: types.optional(types.enumeration(['red', 'yellow', 'green', 'notGraded']), 'notGraded'),
    reviewer: types.maybe(User),
    isDirty: types.optional(types.boolean, false),
    pointsDescriptions: types.maybe(types.array(_pointsDescription)),
    numberOfTries: types.optional(types.number, 1),
    ignoredQuestions: types.maybe(types.number),
    murphyInteractionStatus: types.maybe(types.enumeration(['ignored', 'responded'])),
    points: types.maybe(types.number),
    interactionId: types.maybe(types.string),
    shareToken: types.maybe(types.string),
    shareTokenExpiresAt: types.maybe(types.string),
    qaAnalysis: types.maybe(types.frozen()),
    gradingStatus: types.maybe(types.string),
    recordingKey: types.maybe(types.string),
    // Live-meeting-only fields. botId is the join key the transcript fetch
    // endpoint requires; the rest populate the transcript PDF header. Set by
    // analyzer.js#saveAssessmentToDynamoDB; absent on practice-pitch
    // assessments.
    botId: types.maybe(types.string),
    meetingTitle: types.maybe(types.string),
    meetingStartTime: types.maybe(types.string),
    duration: types.maybe(types.number)
})
    .preProcessSnapshot(snapshot => {
        const sanitizeUser = (u) => {
            if (!u) return undefined;
            const raw = isStateTreeNode(u) ? getSnapshot(u) : u;
            return {
                ...raw,
                email: raw.email || '',
                userType: raw.userType || 'Sales Member',
            };
        };

        return {
            ...snapshot,
            numberOfTries: snapshot.numberOfTries != null ? Number(snapshot.numberOfTries) : undefined,
            sessionType: normalizeSessionType(snapshot.sessionType),
            subject: normalizeSubject(snapshot.subject),
            // Ensure salesperson always has required User fields.
            salesperson: sanitizeUser(snapshot.salesperson) ?? { userId: '', firstName: 'Unknown', lastName: 'User', email: '', userType: 'Sales Member' },
            // Sanitize optional reviewer the same way.
            reviewer: sanitizeUser(snapshot.reviewer),
            // Sanitize all types.maybe(string) fields — DynamoDB null must not reach MST union checks.
            secondarySubject: toMaybeString(snapshot.secondarySubject),
            reviewDateTime: toMaybeString(snapshot.reviewDateTime),
            videoName: toMaybeString(snapshot.videoName),
            completedDateTime: toMaybeString(snapshot.completedDateTime),
            additionalAttendees: toMaybeString(snapshot.additionalAttendees),
            offeringPositives: toMaybeString(snapshot.offeringPositives),
            offeringAreasForImprovement: toMaybeString(snapshot.offeringAreasForImprovement),
            companyBackgroundPositives: toMaybeString(snapshot.companyBackgroundPositives),
            companyBackgroundAreasForImprovement: toMaybeString(snapshot.companyBackgroundAreasForImprovement),
            differentiatorsPositives: toMaybeString(snapshot.differentiatorsPositives),
            differentiatorsAreasForImprovement: toMaybeString(snapshot.differentiatorsAreasForImprovement),
            prospectProfilePositives: toMaybeString(snapshot.prospectProfilePositives),
            prospectProfileAreasForImprovement: toMaybeString(snapshot.prospectProfileAreasForImprovement),
            topCompetitorsPositives: toMaybeString(snapshot.topCompetitorsPositives),
            topCompetitorsAreasForImprovement: toMaybeString(snapshot.topCompetitorsAreasForImprovement),
            companyOverviewPositives: toMaybeString(snapshot.companyOverviewPositives),
            companyOverviewAreasForImprovement: toMaybeString(snapshot.companyOverviewAreasForImprovement),
            interactionId: toMaybeString(snapshot.interactionId),
            gradingStatus: toMaybeString(snapshot.gradingStatus),
            recordingKey: toMaybeString(snapshot.recordingKey),
            botId: toMaybeString(snapshot.botId),
            meetingTitle: toMaybeString(snapshot.meetingTitle),
            meetingStartTime: toMaybeString(snapshot.meetingStartTime),
            // Sanitize types.maybe(number) fields.
            ignoredQuestions: toMaybeNumber(snapshot.ignoredQuestions),
            points: toMaybeNumber(snapshot.points),
            duration: toMaybeNumber(snapshot.duration),
            // Sanitize types.maybe(enumeration) — null is not a valid enum value.
            murphyInteractionStatus: (snapshot.murphyInteractionStatus === 'ignored' || snapshot.murphyInteractionStatus === 'responded')
                ? snapshot.murphyInteractionStatus
                : undefined,
            // Sanitize pointsDescriptions items so nested maybe fields also survive nulls.
            // The REQUIRED fields are coerced too: the live-meeting analyzer passes
            // LLM output straight through, and one null category/description in one
            // assessment would make applySnapshot throw for the WHOLE assessments
            // array — every dashboard then shows zero sessions company-wide.
            pointsDescriptions: Array.isArray(snapshot.pointsDescriptions)
                ? snapshot.pointsDescriptions.map(pd => ({
                    ...pd,
                    category: typeof pd.category === 'string' ? pd.category : 'General',
                    description: typeof pd.description === 'string' ? pd.description : '',
                    pointsAwarded: toMaybeNumber(pd.pointsAwarded) ?? 0,
                    maxPoints: toMaybeNumber(pd.maxPoints),
                    skipped: toMaybeBoolean(pd.skipped),
                    keyPointsCovered: Array.isArray(pd.keyPointsCovered) ? pd.keyPointsCovered : undefined,
                    keyPointsMissed: Array.isArray(pd.keyPointsMissed) ? pd.keyPointsMissed : undefined,
                }))
                : undefined,
        };
    })
    .views(self => ({
        get areasForImprovementView() {
            if (!!self.pointsDescriptions) {
                return self.pointsDescriptions.filter(isDisplayableCategory).filter(point => {
                    if (point.maxPoints) {
                        return point.pointsAwarded / point.maxPoints < 0.65;
                    }
                    return point.pointsAwarded === 0;
                }).map(point => point.description) ?? [];
            }
            return self.companyOverviewAreasForImprovement?.split('- ').slice(1) ?? [];
        },
        get positivesView() {
            if (!!self.pointsDescriptions) {
                return self.pointsDescriptions.filter(isDisplayableCategory).filter(point => {
                    if (point.maxPoints) {
                        return point.pointsAwarded / point.maxPoints >= 0.65;
                    }
                    return point.pointsAwarded > 0;
                }).map(point => point.description) ?? [];
            }
            return self.companyOverviewPositives?.split('- ').slice(1) ?? [];
        },
        get colorForGrade() {

            if (self.grade === 'notGraded') {
                return 'gray';
            }

            return self.grade;
        },
        get assessmentCanBeSaved() {
            if (self.subject === 'Company Overview') {
                return !!self.companyOverviewPositives &&
                    !!self.companyOverviewAreasForImprovement &&
                    self.grade !== 'notGraded';
            } else {
                return !!self.offeringAreasForImprovement &&
                    !!self.offeringPositives &&
                    self.grade !== 'notGraded';
            }
        },
        get companyOverviewPositivesView() {
            if (!!self.companyOverviewPositives) {
                return self.companyOverviewPositives;
            }

            const positives = [];
            if (!!self.companyBackgroundPositives) {
                positives.push(`${self.companyBackgroundPositives}\n\n`);
            }

            if (!!self.differentiatorsPositives) {
                positives.push(`${self.differentiatorsPositives}\n\n`);
            }

            if (!!self.prospectProfilePositives) {
                positives.push(`${self.prospectProfilePositives}\n\n`);
            }

            if (!!self.topCompetitorsPositives) {
                positives.push(`${self.topCompetitorsPositives}\n\n`);
            }

            return positives.join('');
        },
        get companyOverviewAreasForImprovementView() {
            if (!!self.companyOverviewAreasForImprovement) {
                return self.companyOverviewAreasForImprovement;
            }

            const negatives = [];
            if (!!self.companyBackgroundAreasForImprovement) {
                negatives.push(`${self.companyBackgroundAreasForImprovement}\n\n`);
            }

            if (!!self.differentiatorsAreasForImprovement) {
                negatives.push(`${self.differentiatorsAreasForImprovement}\n\n`);
            }

            if (!!self.prospectProfileAreasForImprovement) {
                negatives.push(`${self.prospectProfileAreasForImprovement}\n\n`);
            }

            if (!!self.topCompetitorsAreasForImprovement) {
                negatives.push(`${self.topCompetitorsAreasForImprovement}\n\n`);
            }

            return negatives.join('');
        }
    }))
    .actions(self => {
        const updateFromEvent = (event) => {
            self[event.target.id] = event.target.value;
            self.isDirty = true;
        };
        const save = flow(function* save() {
            const snapshot = { ...getSnapshot(self) };
            delete snapshot.isDirty;
            self.isDirty = false;
            yield getParent(self, 2).updateAssessmentFromSnapshot(snapshot);
        });
        const setSessionType = (sessionType) => {
            self.sessionType = sessionType;
        };
        return {
            updateFromEvent,
            setSessionType,
            save
        };

    });

export const NullAssessment = Assessment.create({
    id: '',
    companyId: '',
    salesperson: clone(NullUser),
    sessionType: 'Phone Call',
    subject: 'Company Overview',
    reviewDateTime: '',
    completedDateTime: '',
    additionalAttendees: ''
});